import React, { FocusEvent, useEffect, useState } from "react";
import { Formik, Form, Field, ErrorMessage, FormikHelpers, FormikValues } from "formik";
import * as Yup from "yup";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import User from "../../stores/UserStore";
import General from "../../stores/GeneralStore";
import { Container } from "../login.screen/login.styled";
import { Spinner } from "../../components/SpinnerRaw/SpinnerRaw";
import { SpinnerRaw } from "../../components/spinner.component";
import { NotifySuccess } from "../../utils/ShowSuccess";
import { IValidatePassword } from "src/interfaces/IValidatePassword";
import { encrypt } from "../../utils/crypt";
import generalStore from "../../stores/GeneralStore";
import { PASSWORD_VALIDATION_MESSAGES } from "../../utils/constants";
import IValidatePasswordResponse from "src/interfaces/IValidatePasswordResponse";
// import '../../screens/forgotusername.css'
// import "./forgotusername.css"
// Your other imports for custom components and styles

const DEFAULT_SENTINEL_VALUE = "";

const renderButtonContent = (isSubmitting: any) => {
  return isSubmitting ? (
    <Spinner width="30px" height="30px" style={{ color: "white" }} />
  ) : (
    "Submit"
  );
};

// const validationSchema = Yup.object().shape({
//   account: Yup.number().required("UserName is required"), // Use number validation rule
//   confirmpassword: Yup.string().required("Password is required").min(8,'min 8 chracters required'),
//   password: Yup.string().required("Password is required").min(8,'min 8 chracters required'),
// });
const validationSchema = Yup.object().shape({
  account: Yup.string().required("UserName is required"),
  password: Yup.string()
    .required("Password is required")
    .min(8, "min 8 characters required"),
  confirmpassword: Yup.string()
    .required("Password is required")
    .oneOf([Yup.ref("password")], "Passwords must match") // Use null as a fallback
    .min(8, "min 8 characters required"),
});

const ResetPassword = () => {
  const navigate = useNavigate();
  const [message, setMessage] = useState("");
  const [resetCode, setResetCode] = useState<any>();
  const [isChecking, setIsChecking] = useState(true);

  const [isValidLink, setIsValidLink] = useState(false);

  useEffect(() => {
    const validateLink = async () => {
      setIsChecking(true);
      // Get the current URL
      const currentURL = new URL(window.location.href);

      // Extract the "code" parameter from the URL
      const code = currentURL.searchParams.get("code");
      setResetCode(code);

      if (code) {
        try {
          const res = await User.isValidLink(code);
          console.log(res);
          setIsValidLink(res.isValid); // Assuming res is a boolean (true/false)
        } catch (error) {
          console.error("Error validating link:", error);
          setIsValidLink(false);
        }
      } else {
        setIsValidLink(false);
      }
      setIsChecking(false);
    };

    validateLink();
  }, []);

  const initialValues = {
    account: DEFAULT_SENTINEL_VALUE,
    confirmpassword: DEFAULT_SENTINEL_VALUE,
    password: DEFAULT_SENTINEL_VALUE,
  };
  const [loader, setLoader] = useState(false);

  const handleSubmit = async (values: any, setFieldError: (field: string, message: string | undefined) => void,
  setSubmitting: (isSubmitting: boolean) => void) => {
    const data = {
      password: values?.password,
      passwordResetCode: resetCode,
      username: values?.account,
    };
    try {
      if(!(await validatePasswordRemotely(values.password, values.account, setFieldError))) {
        setSubmitting(false);
        return;
      }
      setLoader(true);
      const res = await User.resetPassword({
        errorId: "resetPassword",
        data,
      });
      if (res?.message == "Success") {
        NotifySuccess(
          General?.codeLabels["cm_resetconmess"]?.label
            ? General?.codeLabels["cm_resetconmess"]?.label
            : "Your password has been reset"
        );
        navigate("/");
      }
    } catch (error) {
      setLoader(false);
      setMessage("An error occurred while processing your request.");
    }
  };

  /**
   * Validate password remotely
   * @param password - The password to validate
   * @param userName - The username associated with the password
   * @param setFieldError - Function to set field errors
   * @returns Promise<void>
   */
  const validatePasswordRemotely = async (
    password: string,
    userName: string,
    setFieldError: (field: string, message: string | undefined) => void,
  ): Promise<boolean> => {
    if (!password || !userName) return false;
  
    const payload: IValidatePassword = {
      password: encrypt(password),
      username: encrypt(userName),
    };
  
    try {
      const response: IValidatePasswordResponse = await User.validatePassword({
        errorId: "resetPassword",
        data: payload,
      });
  
      const labels = generalStore?.codeLabels;
  
      if (response.isInvalidUserName) {
        setFieldError(
          "account",
          labels?.["cm_invalid_username"]?.label || PASSWORD_VALIDATION_MESSAGES.invalidUserName
        );
        return false;
      }

      if (response.isBlackListed) {
        setFieldError(
          "password",
          labels?.["cm_password_blacklisted"]?.label || PASSWORD_VALIDATION_MESSAGES.blacklisted
        );
        return false;
      }

      if (response.containsUserData) {
        setFieldError(
          "password",
          labels?.["cm_password_security_validation"]?.label || PASSWORD_VALIDATION_MESSAGES.policyNotSatisfied
        );
        return false;
      }

      if (!response.passwordPolicySatisfied) {
        setFieldError(
          "password",
          labels?.["cm_password_security_validation"]?.label || PASSWORD_VALIDATION_MESSAGES.policyNotSatisfied
        );
        return false;
      }
  
      return true; // All checks passed
    } catch (error) {
      setFieldError("password", PASSWORD_VALIDATION_MESSAGES.validationError);
      return false;
    }
  };
  
  const InValidLink = () => {
    return (
      <Box
        sx={{
          width: General?.isLargeScreen ? "600px" : "100%",
          padding: "50px",
          backgroundColor: "#fff",
        }}
      >
        <h2
          style={{
            alignSelf: "center",
            fontWeight: "500",
            fontSize: General?.isLargeScreen
              ? `${2.5 * General.accessibility.fontSize}rem`
              : `${1.5 * General.accessibility.fontSize}rem`,
            color: "#101e8e",
          }}
        >
          {General?.codeLabels["cm_invalid_or_expired_link"]?.label}
        </h2>
        <Typography
          sx={{ fontSize: "1rem", color: "#666", marginBottom: "20px" }}
        >
          {General?.codeLabels["cm_password_reset_expired"]?.label}
        </Typography>
        <button
          style={{
            height: "3rem",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            textAlign: "center",
            fontSize: `${0.875 * General.accessibility.fontSize}rem`,
            alignItems: "center",
            backgroundColor: General.contrast.contrastColor
              ? General?.contrast.contrastColor === "yellow"
                ? "#FFFF00"
                : General.contrast.contrastColor === "lime"
                ? "#00FF00"
                : "#101e8e"
              : General?.ColorAss
              ? General?.ColorAss
              : "",
            cursor: "pointer",
            color:
              General.contrast.contrastColor === "yellow"
                ? "#0000FF"
                : General.contrast.contrastColor === "lime"
                ? "#000"
                : "#fff",
            fontWeight: "700",
            border: "none",
          }}
          onClick={() => navigate("/forgot-password")}
        >
          Reset Password
        </button>
      </Box>
    );
  };

  const ValidLink = () => {
    return (
      <Box
        sx={{
          width: General?.isLargeScreen ? "600px" : "100%",
          padding: "50px",
          backgroundColor: "#fff",
        }}
      >
        <h2
          style={{
            alignSelf: "center",
            fontWeight: "500",
            fontSize: General?.isLargeScreen
              ? `${2.5 * General.accessibility.fontSize}rem`
              : `${1.5 * General.accessibility.fontSize}rem`,
            color: "#101e8e",
          }}
        >
          {General?.codeLabels["cm_resetbtn"]?.label}
        </h2>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(e, {setFieldError, setSubmitting}) => {handleSubmit(e, setFieldError, setSubmitting)}}
        >
          {({
            isSubmitting,
            isValid,
            dirty,
            handleBlur,
            setFieldError,
            values,
          }) => (
            <Form>
              <Box sx={{ marginBottom: "16px" }}>
                <label
                  htmlFor="account"
                  style={{
                    fontSize: `${0.8 * General.accessibility.fontSize}rem`,
                    color: "#101e8e",
                  }}
                >
                  UserName
                </label>
                <Field
                  type="text"
                  name="account"
                  className="input-field"
                  placeholder="UserName"
                  style={{
                    width: "100%",
                    height: "40px",
                    padding: "8px",
                    border: "1px solid rgba(182, 191, 220, 1)",
                    backgroundColor: "rgba(229, 239, 242, 0.7)",
                    color: "#101e8e",
                  }}
                />
                <ErrorMessage
                  name="account"
                  component="div"
                  className="error-message"
                />
              </Box>

              <Box sx={{ marginBottom: "16px" }}>
                <label
                  htmlFor="email"
                  style={{
                    fontSize: `${0.8 * General.accessibility.fontSize}rem`,
                    color: "#101e8e",
                  }}
                >
                  Password
                </label>
                <Field
                  type="password"
                  name="password"
                  className="input-field"
                  placeholder="Password"
                  style={{
                    width: "100%",
                    height: "40px",
                    padding: "8px",

                    border: "1px solid rgba(182, 191, 220, 1)",
                    backgroundColor: "rgba(229, 239, 242, 0.7)",
                    color: "#101e8e",
                  }}
                    validate={async (value: string) => {
                    if (!value || value.length < 8) return "Min 8 characters required";
                
                    const deviceAccount = values.account; // You might pass this in from form context
                
                    const payload: IValidatePassword = {
                      password: encrypt(value),
                      username: encrypt(deviceAccount),
                    };
                
                    try {
                      const response: IValidatePasswordResponse = await User.validatePassword({
                        errorId: "resetPassword",
                        data: payload,
                      });
                
                      if (response.isInvalidUserName) {
                        return generalStore?.codeLabels["cm_invalid_username"]?.label ?? PASSWORD_VALIDATION_MESSAGES.invalidUserName;
                      }
                
                      if (response.isBlackListed) {
                        return generalStore?.codeLabels["cm_password_blacklisted"]?.label ?? PASSWORD_VALIDATION_MESSAGES.blacklisted;
                      }
                
                      if (!response.passwordPolicySatisfied) {
                        return generalStore?.codeLabels["cm_password_security_validation"]?.label ?? PASSWORD_VALIDATION_MESSAGES.policyNotSatisfied;
                      }
                
                      return undefined;
                    } catch {
                      return PASSWORD_VALIDATION_MESSAGES.validationError;
                    }
                  }}
                />
                <ErrorMessage
                  name="password"
                  component="div"
                  className="error-message"
                />
              </Box>
              <Box sx={{ marginBottom: "16px" }}>
                <label
                  htmlFor="confirmpassword"
                  style={{
                    fontSize: `${0.8 * General.accessibility.fontSize}rem`,
                    color: "#101e8e",
                  }}
                >
                  Confirm Password
                </label>
                <Field
                  type="password"
                  name="confirmpassword"
                  className="input-field"
                  placeholder="confirm password"
                  style={{
                    width: "100%",
                    height: "40px",
                    padding: "8px",

                    border: "1px solid rgba(182, 191, 220, 1)",
                    backgroundColor: "rgba(229, 239, 242, 0.7)",
                    color: "#101e8e",
                  }}
              
                />
                <ErrorMessage
                  name="confirmpassword"
                  component="div"
                  className="error-message"
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  marginTop: "40px",
                  gap: "10px",
                }}
              >
                <button
                  type="submit"
                  disabled={isSubmitting || !(isValid && dirty)}
                  style={{
                    height: "3rem",
                    width: General?.isLargeScreen ? "132px" : "100%",
                    display: "flex",
                    justifyContent: "center",
                    textAlign: "center",
                    fontSize: `${0.875 * General.accessibility.fontSize}rem`,
                    alignItems: "center",
                    backgroundColor: "#101e8e",
                    cursor: "pointer",
                    color: "white",
                    fontWeight: "700",
                    border: "none",
                    opacity: isSubmitting || !(isValid && dirty) ? 0.6 : 1,
                  }}
                >
                  {loader ? (
                    <SpinnerRaw width={30} height={30} />
                  ) : (
                    renderButtonContent(isSubmitting)
                  )}
                </button>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    );
  };

  if (isChecking) {
    return;
  }

  return (
    <Container>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100%",
        }}
        className="forgotusername-container"
      >
        {isValidLink ? <ValidLink /> : <InValidLink />}
      </Box>
    </Container>
  );
};

export default ResetPassword;
